<script setup lang="ts">
import QueueItemsTable from "~/components/queue/QueueItemsTable.vue";
import ArrivedQueueItem from "~/dto/queue/ArrivedQueueItem";
import QueuesCounts from "~/dto/queue/QueuesCounts";

const { t } = useI18n();

const props = defineProps({
  counts: {
    type: QueuesCounts,
    required: true,
  },
  items: {
    type: Array as () => ArrivedQueueItem[],
    required: true,
  },
  itemsLimit: {
    type: Number,
    default: 50
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  isExpanded: {
    type: Boolean,
    default: false
  }
});
const emit = defineEmits(['expanded', 'collapsed', 'loadingRequested'])

const isExpanded = ref<boolean>(props.isExpanded);
const isLoadingRequested = ref<boolean>(false);

watch(isExpanded, (value) => emit(value ? 'expanded' : 'collapsed'));
watch(() => props.items, (items) => {
  if (0 === items.length) {
    isExpanded.value = false;
  }
});
watch(() => props.isLoading, (isLoading) => {
  if (!isLoading) {
    isLoadingRequested.value = false;
  }
});

const isLoadingAvailable = computed<boolean>(() => props.counts.total > props.items.length);
const isLoading = computed<boolean>(() => isLoadingRequested.value && props.isLoading);
const loadMore = () => {
  isLoadingRequested.value = true;
  emit('loadingRequested', props.itemsLimit + 50);
};
</script>

<template>
  <div class="card">
    <div class="card-body">
      <div v-if="isExpanded">
        <div>
          <a
            class="icon-link float-end"
            href="#"
            @click="isExpanded = false"
          >
            <button
              class="btn btn-close"
              :title="t('components.queue_items.close')"
            />
          </a>
          <h4>{{ t("components.queue_items.text") }}</h4>
        </div>
        <QueueItemsTable :items="items" />
        <div
          v-if="isLoadingAvailable || items.length > 20"
          class="d-flex align-items-end justify-content-between"
        >
          <div>
            <button
              v-if="isLoadingAvailable"
              class="btn btn-primary"
              :disabled="isLoading"
              @click="loadMore"
            >
              <span
                v-if="isLoading"
                class="spinner-border spinner-border-sm"
                aria-hidden="true"
              />
              <template v-else>
                {{ t('components.queue_items.load_more') }}
              </template>
            </button>
          </div>

          <a
            class="icon-link"
            href="#"
            @click="isExpanded = false"
          >
            <button
              class="btn btn-close"
              :title="t('components.queue_items.close')"
            />
          </a>
        </div>
      </div>
      <div
        v-else
        class="position-relative"
      >
        <div class="fs-4 fw-semibold">
          {{ t('components.queue_items.title', {n: counts.total}) }}
        </div>
        <div>
          {{ t('components.queue_items.text') }}
        </div>
        <button
          v-if="items.length > 0"
          class="btn btn-primary btn-sm position-absolute bottom-0 end-0"
          @click="isExpanded = true"
        >
          {{ t('components.queue_items.open') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.btn-sm {
  --bs-btn-padding-y: .25rem;
  --bs-btn-padding-x: .5rem;
  --bs-btn-font-size: .75rem;
}
</style>
